import { useState } from "react";
import { Field, Select, Textarea } from "../form-fields/FormFields";

const PROJECT_TYPES = ['Commercial', 'Industrial', 'Public Works', 'Misc. Metals / Architectural', 'Other'];
const TONNAGE_RANGES = ['Under 100 tons', '100 – 500 tons', '500 – 1,500 tons', '1,500+ tons', 'Not sure yet'];
const SOFTWARE_OPTIONS = ['Tekla PowerFab', 'FabSuite', 'Steel Projects', 'Other / None'];

export const QuoteCTA = () => {
    const [formData, setFormData] = useState({
        name: '', company: '', email: '', phone: '',
        projectType: '', tonnage: '', deadline: '', software: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    return (
        <section id="contact" className="py-20 md:py-28 bg-steel-50">
            <div className="container-px mx-auto max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-2">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Request a quote</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-5">
                            Tell us about your project.
                        </h2>
                        <p className="text-lg text-steel-600 leading-relaxed mb-10">
                            Share a few details and we'll respond with scope, timeline, and pricing within 48 hours.
                            For complex projects, expect a quick call to align on the deliverables.
                        </p>

                        <div className="space-y-6 pt-6 border-t border-steel-200">
                            <ContactRow
                                label="Jim Truitt — CEO"
                                lines={[
                                    { type: 'tel', value: '(858) 344-2140', display: '(858) 344-2140' },
                                    { type: 'mail', value: 'jim@aircad.net', display: 'jim@aircad.net' },
                                    { type: 'mail', value: 'jim@rockypointdetailing.com', display: 'jim@rockypointdetailing.com' },
                                ]}
                            />
                            <ContactRow
                                label="Office"
                                lines={[
                                    { type: 'text', display: '1605 Palmales Ct.' },
                                    { type: 'text', display: 'Las Vegas, NV 89128' },
                                    { type: 'tel', value: '(702) 430-8892', display: '(702) 430-8892' },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-2xl shadow-card border border-steel-100 p-6 md:p-10">
                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-accent/20 text-navy-700 flex items-center justify-center mx-auto mb-5">
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold text-navy-900 font-display mb-2">Thanks — we'll be in touch.</h3>
                                    <p className="text-steel-600">We'll respond within 48 hours with next steps.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <Field label="Your name" name="name" value={formData.name} onChange={handleChange} required />
                                        <Field label="Company" name="company" value={formData.company} onChange={handleChange} />
                                        <Field label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                                        <Field label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <Select label="Project type" name="projectType" value={formData.projectType} onChange={handleChange} options={PROJECT_TYPES} required />
                                        <Select label="Approx. tonnage" name="tonnage" value={formData.tonnage} onChange={handleChange} options={TONNAGE_RANGES} />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <Field label="Target start / deadline" name="deadline" value={formData.deadline} onChange={handleChange} placeholder="e.g. Q3 2026, ASAP, flexible" />
                                        <Select label="Fab software" name="software" value={formData.software} onChange={handleChange} options={SOFTWARE_OPTIONS} />
                                    </div>

                                    <Textarea
                                        label="Project details"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={4}
                                        placeholder="Brief scope, location, special requirements, anything you'd like us to know."
                                    />

                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                                        <p className="text-xs text-steel-500">
                                            By submitting, you agree to be contacted about your project inquiry.
                                        </p>
                                        <button
                                            type="submit"
                                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-card hover:shadow-card-hover w-full sm:w-auto"
                                        >
                                            Send request
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ContactRow = ({ label, lines }) => (
    <div>
        <p className="text-xs font-semibold text-steel-500 uppercase tracking-wider mb-2">{label}</p>
        <div className="space-y-1">
            {lines.map((line, i) => {
                if (line.type === 'tel') return <a key={i} href={`tel:${line.value.replace(/[^\d]/g, '')}`} className="block text-navy-900 font-medium no-underline hover:text-navy-600 transition-colors">{line.display}</a>;
                if (line.type === 'mail') return <a key={i} href={`mailto:${line.value}`} className="block text-navy-900 font-medium no-underline hover:text-navy-600 transition-colors">{line.display}</a>;
                return <p key={i} className="text-navy-900 font-medium m-0">{line.display}</p>;
            })}
        </div>
    </div>
);
